<template>
  <div class="policy-inquiry-page">
    <!-- 页面标题 -->
    <div class="page-header">
      <h1 class="page-title">保单查询</h1>
      <p class="page-subtitle">查看、管理您的旅游意外险保单信息</p>
    </div>

    <!-- 保单列表 -->
    <div class="policy-list" v-if="policyList.length > 0">
      <div 
        v-for="policy in policyList" 
        :key="policy.id"
        class="policy-card"
      >
        <!-- 卡片头部 -->
        <div class="card-header">
          <div class="header-left">
            <span class="status-tag" :class="getStatusClass(policy.coverStatus)">
              {{ policy.coverStatusStr }}
            </span>
            <span class="policy-no">保单号：{{ policy.insurerPolicyCode }}</span>
          </div>
          <div class="header-right">
            <span class="apply-date">投保日期：{{ policy.createTime }}</span>
          </div>
        </div>

        <!-- 卡片内容 -->
        <div class="card-body">
          <div class="info-row">
            <div class="info-item">
              <span class="label">保险名称：</span>
              <span class="value">{{ policy.productName }}</span>
            </div>
            <div class="info-item">
              <span class="label">投保单位：</span>
              <span class="value">{{ policy.name }}</span>
            </div>
            <div class="info-item">
              <span class="label">被保人数：</span>
              <span class="value">{{ policy.insuredCount }}人</span>
            </div>
            <div class="info-item">
              <span class="label">保费：</span>
              <span class="value">{{ policy.premium }}元</span>
            </div>
          </div>
          <div class="info-row">
            <div class="info-item full-width">
              <span class="label">保障期限：</span>
              <span class="value">{{ policy.effectiveDate }} 至 {{ policy.expiryDate }}</span>
            </div>
          </div>
        </div>

        <!-- 卡片操作 -->
        <div class="card-footer">
          <template>
            <v-btn 
              v-if="policy.coverStatus !== 8"
              color="#dd122a" 
              class="action-btn"
              small
              :loading="downloadingId === policy.id"
              @click="downloadPolicy(policy)"
            >
              下载电子保单
            </v-btn>
            <v-btn 
              v-if="policy.coverStatus !== 9 && policy.coverStatus !== 8"
              color="#dd122a" 
              class="action-btn"
              small
              @click="applyRefund(policy)"
            >
              申请退保
            </v-btn>
            <v-btn 
              color="#dd122a" 
              class="action-btn outline-btn"
              small
              @click="viewPersonList(policy)"
            >
              查看人员清单
            </v-btn>
            <v-btn 
              v-if="policy.coverStatus == 8"
              color="#dd122a" 
              class="action-btn outline-btn"
              small
              @click="viewRefundRecord(policy)"
            >
              退保记录
            </v-btn>
          </template>
        </div>
      </div>
    </div>

    <!-- 空状态 -->
    <div class="empty-state" v-else>
      <v-icon size="64" color="#ddd">description</v-icon>
      <p class="empty-text">暂无保单记录</p>
    </div>

    <!-- 分页 -->
    <div class="pagination-wrapper" v-if="policyList.length > 0">
      <v-pagination
        v-model="currentPage"
        :length="totalPages"
        :total-visible="7"
        color="#dd122a"
        @input="handlePageChange"
      ></v-pagination>
    </div>

    <!-- 申请退保弹窗 -->
    <v-dialog v-model="refundDialog.visible" max-width="400">
      <v-card>
        <v-card-title class="dialog-title">申请退保</v-card-title>
        <v-card-text class="dialog-content">
          <p>尊敬的客户，本保单已生效，如需办理退保业务，请直接联系保险公司工作人员进行申请。</p>
          <p class="contact-info">联系人：张老师</p>
          <p class="contact-info">联系电话：022-58817632</p>
        </v-card-text>
        <v-card-actions class="dialog-actions">
          <v-btn color="#dd122a" dark @click="refundDialog.visible = false">我知道了</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 退保二次确认弹窗 -->
    <v-dialog v-model="confirmRefundDialog.visible" max-width="400">
      <v-card>
        <v-card-title class="dialog-title">确认退保</v-card-title>
        <v-card-text class="dialog-content">
          <p>您确定要为此保单申请退保吗？</p>
          <p class="policy-info">保单号：{{ confirmRefundDialog.policyNo }}</p>
        </v-card-text>
        <v-card-actions class="dialog-actions">
          <v-btn @click="confirmRefundDialog.visible = false" :disabled="confirmRefundDialog.loading">取消</v-btn>
          <v-btn color="#dd122a" dark @click="confirmRefund" :loading="confirmRefundDialog.loading">确认退保</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 查看人员清单弹窗 -->
    <PersonListDialog ref="personListDialog"/>

    <!-- 退保记录弹窗 -->
    <RefundRecordDialog ref="refundRecordDialog"/>
  </div>
</template>

<script>
import PersonListDialog from './personListDialog.vue';
import RefundRecordDialog from './refundRecordDialog.vue';
import { getPolicyList, getElectronicPolicy, standardCancel } from '@/api/travelInsurance';

export default {
  name: 'PolicyInquiry',
  components: {
    PersonListDialog,
    RefundRecordDialog
  },
  data() {
    return {
      // 保单列表
      policyList: [],
      currentPage: 1,
      pageSize: 5,
      totalPages: 1,
      totalCount: 0,
      downloadingId: null,

      // 申请退保弹窗
      refundDialog: {
        visible: false,
        policyNo: '',
        policyId: null
      },

      // 退保二次确认弹窗
      confirmRefundDialog: {
        visible: false,
        policyNo: '',
        policyId: null,
        loading: false
      },
     
    };
  },
  mounted() {
    this.loadPolicyList();
  },
  methods: {
    // 加载保单列表
    async loadPolicyList() {
      const certNo = this.$route.query.certNo;
      if (!certNo) {
        this.policyList = [];
        return;
      }
      
      try {
        const params = {
          certNo,
          pageIndex: this.currentPage,
          pageSize: this.pageSize
        };
        const res = await getPolicyList(params);
        if (res.code === 0 || res.success) {
          const pageData = res.data || {};
          this.policyList = pageData.records || [];
          this.totalCount = pageData.total || 0;
          this.totalPages = pageData.pages || 1;
        } else {
          this.$message.error(res.msg || '获取保单列表失败');
          this.policyList = [];
        }
      } catch (error) {
        console.error('获取保单列表失败:', error);
        this.$message.error('获取保单列表失败');
        this.policyList = [];
      }
    },

    // 获取状态样式类
    getStatusClass(status) {
      const statusMap = {
        0: 'status-pending',
        5: 'status-active',
        9: 'status-expired',
        8: 'status-canceled'
      };
      return statusMap[status] || '';
    },

    // 分页变化
    handlePageChange(page) {
      this.currentPage = page;
      this.loadPolicyList();
    },

    // 下载电子保单
    async downloadPolicy(policy) {
      this.downloadingId = policy.id;
      try {
        const res = await getElectronicPolicy(policy.id);
        if (res.code === 0 || res.success) {
          // 下载PDF文件
          if (res.data) {
            const link = document.createElement('a');
            link.href = res.data;
            link.download = `保单_${policy.policyNo}.pdf`;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        } else {
          this.$message.error(res.msg || '下载失败');
        }
      } catch (error) {
        console.error('下载电子保单失败:', error);
        this.$message.error('下载电子保单失败');
      } finally {
        this.downloadingId = null;
      }
    },

    // 申请退保
    applyRefund(policy) {
       // 可以退保，显示二次确认弹窗
      if (policy.coverStatus === 0) {
        this.confirmRefundDialog.policyNo = policy.insurerPolicyCode;
        this.confirmRefundDialog.policyId = policy.id;
        this.confirmRefundDialog.visible = true;
      } else {
        // 已生效，需要联系保险公司
        this.refundDialog.policyNo = policy.policyNo;
        this.refundDialog.policyId = policy.id;
        this.refundDialog.visible = true;
      }
    },

    // 确认退保
    async confirmRefund() {
      if (this.confirmRefundDialog.loading) return;
      
      this.confirmRefundDialog.loading = true;
      try {
        const res = await standardCancel(this.confirmRefundDialog.policyId);
        if (res.code === 0 || res.success) {
          this.$message.success('退保申请成功');
          this.confirmRefundDialog.visible = false;
          this.loadPolicyList();
        } else {
          this.$message.error(res.msg || '退保申请失败');
        }
      } catch (error) {
        console.error('退保申请失败:', error);
        this.$message.error('退保申请失败');
      } finally {
        this.confirmRefundDialog.loading = false;
      }
    },

    // 查看人员清单
    viewPersonList(policy) {
      this.$refs["personListDialog"].loadList(policy)
    },

    // 搜索人员清单
    searchPersonList(searchKey) {
      console.log('搜索：', searchKey);
      // 调用搜索接口
    },

    // 导出人员清单
    exportPersonList() {
      console.log('导出人员清单');
      // 调用导出接口
    },

    // 查看退保记录
    viewRefundRecord(policy) {
      this.$refs["refundRecordDialog"].onLoad(policy);
    },
  }
};
</script>

<style lang="scss" scoped>
.policy-inquiry-page {
  min-height: 100vh;
  background-color: #f5f5f5;
  padding: 30px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;

  > * {
    width: 100%;
    max-width: 1200px;
  }
}

// 页面标题
.page-header {
  margin-bottom: 24px;

  .page-title {
    font-size: 24px;
    font-weight: 600;
    color: #000;
  }

  .page-subtitle {
    font-size: 16px;
    color: #666;
  }
}

// 保单列表
.policy-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 20px;
}

// 保单卡片
.policy-card {
  background-color: #fff;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 16px;
    border-bottom: 1px solid #E8E8E8;

    .header-left {
      display: flex;
      align-items: center;
      gap: 20px;

      .status-tag {
        padding: 2px 6px;
        border-radius: 4px;
        font-size: 12px;
        font-weight: 600;

        &.status-pending {
          background-color: #DBEAFE;
          color: #007DFF;
        }

        &.status-active {
          background-color: #DCFCE8;
          color: #18813E;
        }

        &.status-expired {
          background-color: #FDF1F2;
          color: #DD122A;
        }

        &.status-canceled {
          background-color: #EEEEEE;
          color: #93969B;
        }
      }

      .policy-no {
        font-size: 14px;
        color: #262626;
        font-weight: 500;
      }
    }

    .header-right {
      .apply-date {
        font-size: 14px;
        color: #262626;
      }
    }
  }

  .card-body {
    padding: 16px 20px;
    .info-row {
      display: flex;
      justify-content: space-between;
      margin-bottom: 12px;

      &:last-child {
        margin-bottom: 0;
      }

      .info-item {
        display: flex;
        align-items: center;
        flex: 1;

        &.full-width {
          width: 100%;
          flex: none;
        }

        .label {
          font-size: 14px;
          color: #262626;
        }

        .value {
          font-size: 14px;
          color: #93969B;
        }
      }
    }
  }

  .card-footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;

    .action-btn {
      text-transform: none;
      font-size: 13px;
      height: 36px;
      border-radius: 4px;
      padding: 0 20px;
      color: #fff;

      &.outline-btn {
        border: 1px solid #dd122a !important;
        color: #dd122a;
        background-color: rgba(221, 18, 42, 0.1) !important;

        &:hover {
          background-color: #fff5f5;
        }
      }
    }
  }
}

// 空状态
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  background-color: #fff;
  border-radius: 8px;

  .empty-text {
    margin-top: 16px;
    font-size: 14px;
    color: #999;
  }
}

// 分页
.pagination-wrapper {
  display: flex;
  justify-content: center;
  padding: 20px 0;
}

// 弹窗样式
.dialog-title {
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

.dialog-content {
  padding: 20px;

  p {
    margin-bottom: 8px;
    font-size: 14px;
    color: #666;

    &.policy-info {
      color: #333;
      font-weight: 500;
    }

    &.contact-info {
      color: #333;
      font-weight: 500;
      margin-bottom: 4px;
    }
  }
}

.dialog-actions {
  padding: 12px 20px;
  justify-content: flex-end;
}
</style>
